import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, errorMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { MultiCombobox } from "@/components/Combobox";
import { Panel } from "@/components/Panel";
import { fmtFecha } from "@/pages/TrabajosActivos";
import { AlertTriangle, Download, Upload, X } from "lucide-react";

const TABS_CONFIG = [
  { value: "taller", label: "Taller" },
  { value: "mano-obra", label: "Mano de obra" },
  { value: "fotos", label: "Fotos" },
  { value: "notificaciones", label: "Notificaciones" },
  { value: "sistema", label: "Sistema" },
];

const Campo = ({ label, children }) => (
  <div className="space-y-1.5">
    <label className="text-sm font-medium text-slate-700">{label}</label>
    {children}
  </div>
);

export default function ConfigTaller() {
  const [cfg, setCfg] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => { api.get("/config-taller").then((r) => setCfg(r.data)); }, []);
  if (!cfg) return <div className="text-sm text-slate-500">Cargando...</div>;
  const set = (k, v) => setCfg({ ...cfg, [k]: v });

  const tabParam = searchParams.get("tab");
  const tabActiva = TABS_CONFIG.some((t) => t.value === tabParam) ? tabParam : "taller";
  const cambiarTab = (v) => setSearchParams(v === "taller" ? {} : { tab: v });

  const subirLogo = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 500 * 1024) { toast.error("El logo debe pesar menos de 500 KB"); return; }
    const reader = new FileReader();
    reader.onload = () => set("logo", reader.result);
    reader.readAsDataURL(f);
  };

  const guardar = async () => {
    setGuardando(true);
    try {
      const { id, ...body } = cfg;
      const r = await api.put("/config-taller", body);
      setCfg(r.data);
      toast.success("Configuración guardada");
    } catch (e) { toast.error(errorMsg(e)); } finally { setGuardando(false); }
  };

  return (
    <div data-testid="config-taller-page" className="space-y-4">
      <Tabs value={tabActiva} onValueChange={cambiarTab}>
        <TabsList data-testid="config-taller-tabs" className="flex-wrap h-auto">
          {TABS_CONFIG.map((t) => (
            <TabsTrigger key={t.value} value={t.value} data-testid={`config-tab-${t.value}`}>
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="taller" className="space-y-4">
          <Panel titulo="Datos del Taller" testId="config-taller-panel">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Campo label="Nombre del taller">
                <Input data-testid="config-nombre-input" value={cfg.nombre} onChange={(e) => set("nombre", e.target.value)} />
              </Campo>
              <Campo label="Rótulo (encabezado de reportes)">
                <Input data-testid="config-rotulo-input" value={cfg.rotulo} onChange={(e) => set("rotulo", e.target.value)} />
              </Campo>
              <Campo label="Teléfono">
                <Input data-testid="config-telefono-input" value={cfg.telefono} onChange={(e) => set("telefono", e.target.value)} />
              </Campo>
              <Campo label="Dirección">
                <Input data-testid="config-direccion-input" value={cfg.direccion} onChange={(e) => set("direccion", e.target.value)} />
              </Campo>
              <Campo label="Email">
                <Input data-testid="config-email-input" value={cfg.email} onChange={(e) => set("email", e.target.value)} />
              </Campo>
              <Campo label="Logo (imagen, máx. 500 KB)">
                <div className="flex flex-wrap items-center gap-3">
                  {cfg.logo && <img src={cfg.logo} alt="Logo" data-testid="config-logo-preview" className="h-10 w-10 object-contain border border-slate-200 rounded" />}
                  <input data-testid="config-logo-input" type="file" accept="image/*" onChange={subirLogo} className="text-xs" />
                </div>
              </Campo>
            </div>
            <div className="mt-4 space-y-1.5">
              <label className="text-sm font-medium text-slate-700">Condiciones generales de los talleres de servicio</label>
              <Textarea data-testid="config-condiciones-input" rows={10} value={cfg.condiciones}
                onChange={(e) => set("condiciones", e.target.value)} />
            </div>
            <div className="flex justify-end mt-6">
              <Button data-testid="config-guardar-btn" onClick={guardar} disabled={guardando}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-8">
                {guardando ? "Guardando..." : "Guardar"}
              </Button>
            </div>
          </Panel>
        </TabsContent>

        <TabsContent value="mano-obra" className="space-y-4">
          <Panel titulo="Grupos de tarifa de mano de obra (₡/hora)" testId="grupos-tarifa-panel">
            <GruposTarifa />
          </Panel>
          <Panel titulo="Grupos de cilindrada" testId="grupos-cilindrada-panel">
            <GruposCilindrada />
          </Panel>
          <Panel titulo="Prefijos equivalentes de código de parte" testId="prefijos-equiv-panel">
            <PrefijosEquivalentes />
          </Panel>
        </TabsContent>

        <TabsContent value="fotos" className="space-y-4">
          <PuntosFoto />
        </TabsContent>

        <TabsContent value="notificaciones" className="space-y-4">
          <WhatsappConfig />
          <CorreoConfig />
        </TabsContent>

        <TabsContent value="sistema" className="space-y-4">
          <CopiaPruebas />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function CopiaPruebas() {
  const { isAdmin } = useAuth();
  const [importarHabilitado, setImportarHabilitado] = useState(false);
  const [exportando, setExportando] = useState(false);
  const [archivo, setArchivo] = useState(null);
  const [confirmarTexto, setConfirmarTexto] = useState("");
  const [importando, setImportando] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!isAdmin) return;
    api.get("/admin/copia-pruebas/estado").then((r) => setImportarHabilitado(!!r.data.importar_habilitado)).catch(() => {});
  }, [isAdmin]);

  if (!isAdmin) return null;

  const exportar = async () => {
    setExportando(true);
    try {
      const r = await api.get("/admin/copia-pruebas/exportar", { responseType: "blob" });
      const u = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      const fecha = new Date().toISOString().slice(0, 10);
      a.href = u; a.download = `copia_pruebas_${fecha}.zip`; a.click();
      URL.revokeObjectURL(u);
    } catch (e) { toast.error(errorMsg(e)); } finally { setExportando(false); }
  };

  const importar = async () => {
    if (!archivo) { toast.error("Elegí el archivo .zip de la copia de pruebas"); return; }
    if (confirmarTexto !== "BORRAR") { toast.error('Escribí "BORRAR" para confirmar'); return; }
    const fd = new FormData();
    fd.append("archivo", archivo);
    fd.append("confirmar", confirmarTexto);
    setImportando(true);
    try {
      const { data } = await api.post("/admin/copia-pruebas/importar", fd);
      toast.success("Copia de pruebas importada");
      setArchivo(null); setConfirmarTexto("");
      if (fileRef.current) fileRef.current.value = "";
      logImportResumen(data);
    } catch (e) { toast.error(errorMsg(e)); } finally { setImportando(false); }
  };

  const logImportResumen = (data) => {
    const totales = Object.values(data.conteos || {}).reduce((a, b) => a + b, 0);
    toast.info(`${totales} documentos importados`);
  };

  return (
    <Panel titulo="Copia de pruebas" testId="copia-pruebas-panel">
      <p className="text-xs text-slate-500 mb-3">
        Incluye el catálogo completo y los últimos 300 trabajos con datos de clientes anonimizados.
      </p>
      <Button data-testid="copia-pruebas-exportar-btn" variant="outline" onClick={exportar} disabled={exportando}
        className="border-indigo-500 text-indigo-600 hover:bg-indigo-50">
        <Download size={15} className="mr-1.5" />
        {exportando ? "Exportando..." : "Exportar copia de pruebas"}
      </Button>

      {importarHabilitado && (
        <div className="mt-5 pt-4 border-t border-slate-100 space-y-3" data-testid="copia-pruebas-importar-seccion">
          <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            <AlertTriangle size={14} className="shrink-0 mt-0.5" />
            Esto borra los datos de este ambiente (trabajos, vehículos, clientes, empleados, pedidos,
            cotizaciones y notificaciones) y los reemplaza con el contenido del .zip.
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <input data-testid="copia-pruebas-archivo-input" ref={fileRef} type="file" accept=".zip"
              onChange={(e) => setArchivo(e.target.files?.[0] || null)} className="text-xs" />
            <Input data-testid="copia-pruebas-confirmar-input" placeholder='Escriba "BORRAR" para confirmar'
              value={confirmarTexto} onChange={(e) => setConfirmarTexto(e.target.value)} className="max-w-xs" />
            <Button data-testid="copia-pruebas-importar-btn" onClick={importar}
              disabled={importando || !archivo || confirmarTexto !== "BORRAR"}
              className="bg-red-600 hover:bg-red-700 text-white">
              <Upload size={15} className="mr-1.5" />
              {importando ? "Importando..." : "Importar copia de pruebas"}
            </Button>
          </div>
        </div>
      )}
    </Panel>
  );
}

function WhatsappConfig() {
  const { isAdmin } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [telefonoPrueba, setTelefonoPrueba] = useState("");
  const [probando, setProbando] = useState(false);
  const [envios, setEnvios] = useState([]);
  const [filtroEstado, setFiltroEstado] = useState("");
  const [pagina, setPagina] = useState(1);
  const [meta, setMeta] = useState({ total: 0, total_pages: 1 });
  const PAGE_SIZE = 20;

  const cargar = () => api.get("/config-whatsapp").then((r) => setCfg(r.data)).catch(() => {});
  const cargarEnvios = (estado, page) => {
    const params = { page, page_size: PAGE_SIZE };
    if (estado) params.estado = estado;
    api.get("/config-whatsapp/envios", { params }).then((r) => {
      setEnvios(r.data.items);
      setMeta({ total: r.data.total, total_pages: r.data.total_pages });
      setPagina(r.data.page);
    }).catch(() => {});
  };

  useEffect(() => { if (isAdmin) { cargar(); cargarEnvios(filtroEstado, 1); } }, [isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (isAdmin) cargarEnvios(filtroEstado, 1); }, [filtroEstado]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isAdmin || !cfg) return null;

  const set = (k, v) => setCfg({ ...cfg, [k]: v });

  const guardar = async () => {
    setGuardando(true);
    try {
      const { activo, meta_plantilla_aviso, hora_desde, hora_hasta, tope_hora, tope_dia } = cfg;
      const r = await api.put("/config-whatsapp", {
        activo, meta_plantilla_aviso, hora_desde, hora_hasta,
        tope_hora: Number(tope_hora) || 60, tope_dia: Number(tope_dia) || 300,
      });
      setCfg(r.data);
      toast.success("Configuración de WhatsApp guardada");
    } catch (e) { toast.error(errorMsg(e)); } finally { setGuardando(false); }
  };

  const enviarPrueba = async () => {
    if (!telefonoPrueba.trim()) { toast.error("Ingrese un teléfono para la prueba"); return; }
    setProbando(true);
    try {
      const { data } = await api.post("/config-whatsapp/prueba", { telefono: telefonoPrueba });
      toast.info(`Resultado: ${data.estado}${data.error ? ` (${data.error})` : ""}`);
      cargarEnvios(filtroEstado, 1);
    } catch (e) { toast.error(errorMsg(e)); } finally { setProbando(false); }
  };

  const reintentar = async (id) => {
    try {
      await api.post(`/config-whatsapp/envios/${id}/reintentar`);
      toast.success("Envío puesto en cola otra vez");
      cargarEnvios(filtroEstado, pagina);
    } catch (e) { toast.error(errorMsg(e)); }
  };

  const ESTADO_CLS = {
    enviado: "bg-teal-100 text-teal-700", pendiente: "bg-amber-100 text-amber-800",
    fallido: "bg-pink-100 text-pink-700", incierto: "bg-slate-200 text-slate-700",
    omitido: "bg-indigo-100 text-indigo-700",
  };

  return (
    <Panel titulo="WhatsApp a clientes" testId="config-whatsapp-panel">
      <div className="flex items-center gap-3 mb-4">
        <Switch data-testid="config-wa-activo-switch" checked={cfg.activo} onCheckedChange={(v) => set("activo", v)} />
        <span className="text-sm text-slate-700">Avisar a los clientes por WhatsApp cuando su trabajo quede finalizado</span>
      </div>
      <div className="flex flex-wrap gap-2 mb-4 text-xs">
        <span data-testid="config-wa-configurado"
          className={`px-2.5 py-1 rounded-full font-medium ${cfg.configurado ? "bg-teal-100 text-teal-700" : "bg-pink-100 text-pink-700"}`}>
          WhatsApp (Meta) configurado: {cfg.configurado ? "sí" : "no"}
        </span>
        <span data-testid="config-wa-dry-run"
          className={`px-2.5 py-1 rounded-full font-medium ${cfg.dry_run ? "bg-amber-100 text-amber-800" : "bg-slate-200 text-slate-700"}`}>
          Modo prueba (no envía): {cfg.dry_run ? "sí" : "no"}
        </span>
      </div>
      <div className="space-y-1.5 mb-4">
        <label className="text-sm font-medium text-slate-700">Nombre de la plantilla en Meta</label>
        <Input data-testid="config-wa-meta-plantilla-input" value={cfg.meta_plantilla_aviso || ""}
          onChange={(e) => set("meta_plantilla_aviso", e.target.value)} className="max-w-xs" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        <Campo label="Enviar desde">
          <Input data-testid="config-wa-hora-desde" type="time" value={cfg.hora_desde}
            onChange={(e) => set("hora_desde", e.target.value)} />
        </Campo>
        <Campo label="Enviar hasta">
          <Input data-testid="config-wa-hora-hasta" type="time" value={cfg.hora_hasta}
            onChange={(e) => set("hora_hasta", e.target.value)} />
        </Campo>
        <Campo label="Tope por hora">
          <Input data-testid="config-wa-tope-hora" inputMode="numeric" value={cfg.tope_hora}
            onChange={(e) => set("tope_hora", e.target.value.replace(/\D/g, ""))} />
        </Campo>
        <Campo label="Tope por día">
          <Input data-testid="config-wa-tope-dia" inputMode="numeric" value={cfg.tope_dia}
            onChange={(e) => set("tope_dia", e.target.value.replace(/\D/g, ""))} />
        </Campo>
      </div>
      <div className="flex justify-end mb-6">
        <Button data-testid="config-wa-guardar-btn" onClick={guardar} disabled={guardando}
          className="bg-indigo-600 hover:bg-indigo-700 text-white px-8">
          {guardando ? "Guardando..." : "Guardar"}
        </Button>
      </div>

      <div className="border-t border-slate-100 pt-4 mb-6">
        <label className="text-sm font-medium text-slate-700">Enviar mensaje de prueba</label>
        <div className="flex flex-wrap items-center gap-3 mt-1.5">
          <Input data-testid="config-wa-telefono-prueba" placeholder="Ej: 8888-7777" value={telefonoPrueba}
            onChange={(e) => setTelefonoPrueba(e.target.value)} className="max-w-xs" />
          <Button data-testid="config-wa-enviar-prueba-btn" onClick={enviarPrueba} disabled={probando}
            variant="outline" className="border-green-500 text-green-600 hover:bg-green-50">
            {probando ? "Enviando..." : "Enviar mensaje de prueba"}
          </Button>
        </div>
      </div>

      <div className="border-t border-slate-100 pt-4">
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-medium text-slate-700">Últimos envíos</label>
          <select data-testid="config-wa-filtro-estado" value={filtroEstado}
            onChange={(e) => setFiltroEstado(e.target.value)}
            className="text-xs border border-slate-200 rounded-md px-2 py-1">
            <option value="">Todos los estados</option>
            <option value="pendiente">Pendiente</option>
            <option value="enviado">Enviado</option>
            <option value="omitido">Omitido</option>
            <option value="fallido">Fallido</option>
            <option value="incierto">Incierto</option>
          </select>
        </div>
        <div className="overflow-x-auto">
          <table data-testid="config-wa-envios-tabla" className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase text-slate-500">
                <th className="py-2 pr-3 font-semibold">Fecha</th>
                <th className="py-2 pr-3 font-semibold">Teléfono</th>
                <th className="py-2 pr-3 font-semibold">Estado</th>
                <th className="py-2 pr-3 font-semibold">Error</th>
                <th className="py-2 text-right font-semibold">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {envios.map((e) => (
                <tr key={e.id} data-testid={`config-wa-envio-row-${e.id}`} className="border-b border-slate-100">
                  <td className="py-2 pr-3 text-slate-600">{fmtFecha(e.creado_en)}</td>
                  <td className="py-2 pr-3 font-mono text-xs">{e.telefono}</td>
                  <td className="py-2 pr-3">
                    <span className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold ${ESTADO_CLS[e.estado] || "bg-slate-100 text-slate-600"}`}>
                      {e.estado}
                    </span>
                  </td>
                  <td className="py-2 pr-3 text-xs text-slate-500">{e.error || "—"}</td>
                  <td className="py-2 text-right">
                    {(e.estado === "fallido" || e.estado === "incierto") && (
                      <Button size="sm" variant="outline" data-testid={`config-wa-reintentar-${e.id}`}
                        className="border-indigo-500 text-indigo-600 hover:bg-indigo-50" onClick={() => reintentar(e.id)}>
                        Reintentar
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
              {envios.length === 0 && (
                <tr><td colSpan={5} className="py-6 text-center text-slate-400" data-testid="config-wa-envios-vacio">
                  Sin envíos registrados
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between mt-3 text-xs text-slate-500">
          <Button size="sm" variant="outline" data-testid="config-wa-envios-anterior-btn"
            disabled={pagina <= 1} onClick={() => cargarEnvios(filtroEstado, pagina - 1)}>
            Anterior
          </Button>
          <span data-testid="config-wa-envios-pagina-info">
            Página {pagina} de {meta.total_pages} ({meta.total} envíos)
          </span>
          <Button size="sm" variant="outline" data-testid="config-wa-envios-siguiente-btn"
            disabled={pagina >= meta.total_pages} onClick={() => cargarEnvios(filtroEstado, pagina + 1)}>
            Siguiente
          </Button>
        </div>
      </div>
    </Panel>
  );
}

// Correo de aviso del control de cupones Honda (solo admin): destinatarios + prueba.
function CorreoConfig() {
  const { isAdmin } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [destinatarios, setDestinatarios] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [correoPrueba, setCorreoPrueba] = useState("");
  const [probando, setProbando] = useState(false);

  useEffect(() => {
    if (!isAdmin) return;
    api.get("/config-correo").then((r) => {
      setCfg(r.data);
      setDestinatarios((r.data.destinatarios || []).join(", "));
    }).catch(() => {});
  }, [isAdmin]);

  if (!isAdmin || !cfg) return null;

  const guardar = async () => {
    setGuardando(true);
    try {
      const lista = destinatarios.split(",").map((d) => d.trim()).filter(Boolean);
      const r = await api.put("/config-correo", { activo: cfg.activo, destinatarios: lista });
      setCfg(r.data);
      setDestinatarios((r.data.destinatarios || []).join(", "));
      toast.success("Configuración de correo guardada");
    } catch (e) { toast.error(errorMsg(e)); } finally { setGuardando(false); }
  };

  const enviarPrueba = async () => {
    if (!correoPrueba.trim()) { toast.error("Ingrese un correo para la prueba"); return; }
    setProbando(true);
    try {
      await api.post("/config-correo/prueba", { destinatario: correoPrueba.trim() });
      toast.success("Correo de prueba enviado");
    } catch (e) { toast.error(errorMsg(e)); } finally { setProbando(false); }
  };

  return (
    <Panel titulo="Correo (control de cupones Honda)" testId="config-correo-panel">
      <div className="flex items-center gap-3 mb-4">
        <Switch data-testid="config-correo-activo-switch" checked={cfg.activo}
          onCheckedChange={(v) => setCfg({ ...cfg, activo: v })} />
        <span className="text-sm text-slate-700">
          Avisar por correo cuando la verificación de cupones Honda encuentre algún problema
        </span>
      </div>
      <div className="mb-4">
        <span data-testid="config-correo-configurado"
          className={`px-2.5 py-1 rounded-full text-xs font-medium ${cfg.configurado ? "bg-teal-100 text-teal-700" : "bg-pink-100 text-pink-700"}`}>
          Servidor SMTP configurado: {cfg.configurado ? "sí" : "no"}
        </span>
      </div>
      <Campo label="Destinatarios (separados por coma)">
        <Input data-testid="config-correo-destinatarios-input" value={destinatarios}
          onChange={(e) => setDestinatarios(e.target.value)} placeholder="taller@ejemplo.com, jefe@ejemplo.com" />
      </Campo>
      <div className="flex justify-end mt-4 mb-6">
        <Button data-testid="config-correo-guardar-btn" onClick={guardar} disabled={guardando}
          className="bg-indigo-600 hover:bg-indigo-700 text-white px-8">
          {guardando ? "Guardando..." : "Guardar"}
        </Button>
      </div>
      <div className="border-t border-slate-100 pt-4">
        <label className="text-sm font-medium text-slate-700">Enviar correo de prueba</label>
        <div className="flex flex-wrap items-center gap-3 mt-1.5">
          <Input data-testid="config-correo-prueba-input" placeholder="correo@ejemplo.com" value={correoPrueba}
            onChange={(e) => setCorreoPrueba(e.target.value)} className="max-w-xs" />
          <Button data-testid="config-correo-enviar-prueba-btn" onClick={enviarPrueba} disabled={probando}
            variant="outline" className="border-green-500 text-green-600 hover:bg-green-50">
            {probando ? "Enviando..." : "Enviar correo de prueba"}
          </Button>
        </div>
      </div>
    </Panel>
  );
}


function GruposCilindrada() {
  const [grupos, setGrupos] = useState([]);
  const [nuevo, setNuevo] = useState("");
  const [servicios, setServicios] = useState([]);

  const cargar = () => api.get("/grupos-cilindrada").then((r) => setGrupos(r.data)).catch(() => {});
  useEffect(() => {
    cargar();
    api.get("/servicios").then((r) => setServicios(r.data.filter((s) => s.activo !== false))).catch(() => {});
  }, []);

  const crear = async () => {
    try {
      await api.post("/grupos-cilindrada", { nombre: nuevo });
      toast.success("Grupo de cilindrada creado");
      setNuevo("");
      cargar();
    } catch (e) { toast.error(errorMsg(e)); }
  };

  const actualizar = async (g) => {
    const cupones = (g.cupones || []).filter((c) => c.servicio_id).map((c) => ({ servicio_id: c.servicio_id, km: Number(c.km) || 0 }));
    if (cupones.some((c) => !(c.km > 0))) { toast.error("Cada cupón necesita un kilometraje mayor a 0"); return; }
    try {
      await api.patch(`/grupos-cilindrada/${g.id}`, {
        nombre: g.nombre, cupones,
        cupones_km_max: g.cupones_km_max ? Number(g.cupones_km_max) : null,
      });
      toast.success("Grupo actualizado");
      cargar();
    } catch (e) { toast.error(errorMsg(e)); }
  };

  const eliminar = async (g) => {
    if (!window.confirm(`¿Eliminar el grupo "${g.nombre}"? Los modelos asignados quedarán sin grupo.`)) return;
    try { await api.delete(`/grupos-cilindrada/${g.id}`); toast.success("Grupo eliminado"); cargar(); }
    catch (e) { toast.error(errorMsg(e)); }
  };

  const setG = (id, campo, v) => setGrupos((prev) => prev.map((g) => (g.id === id ? { ...g, [campo]: v } : g)));
  const agregarCupon = (id) => setGrupos((prev) => prev.map((g) => (g.id === id
    ? { ...g, cupones: [...(g.cupones || []), { servicio_id: "", km: "" }] } : g)));
  const quitarCupon = (id, idx) => setGrupos((prev) => prev.map((g) => (g.id === id
    ? { ...g, cupones: (g.cupones || []).filter((_, i) => i !== idx) } : g)));
  const cambiarCupon = (id, idx, campo, valor) => setGrupos((prev) => prev.map((g) => (g.id === id
    ? { ...g, cupones: (g.cupones || []).map((c, i) => (i === idx ? { ...c, [campo]: valor } : c)) } : g)));

  return (
    <div className="space-y-4" data-testid="grupos-cilindrada-lista">
      <p className="text-xs text-slate-500 mb-1">Agrupe los modelos por cilindrada para seleccionarlos en bloque en "Modelos compatibles" de plantillas y repuestos, y para configurar sus cupones de mantenimiento.</p>
      {grupos.map((g) => (
        <div key={g.id} data-testid={`grupo-cil-fila-${g.id}`} className="border border-slate-100 rounded-lg p-3 space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <Input value={g.nombre} className="max-w-xs" data-testid={`grupo-cil-nombre-${g.id}`}
              onChange={(e) => setG(g.id, "nombre", e.target.value)} />
            <Button size="sm" variant="outline" data-testid={`grupo-cil-guardar-${g.id}`}
              className="border-teal-500 text-teal-600 hover:bg-teal-50" onClick={() => actualizar(g)}>Guardar</Button>
            <Button size="sm" variant="outline" data-testid={`grupo-cil-eliminar-${g.id}`}
              className="border-pink-300 text-pink-600 hover:bg-pink-50" onClick={() => eliminar(g)}>Eliminar</Button>
          </div>

          <div className="pl-3 border-l-2 border-slate-100 space-y-2">
            <p className="text-xs font-medium text-slate-600">Cupones de mantenimiento</p>
            {(g.cupones || []).map((c, idx) => (
              <div key={idx} className="flex flex-wrap items-center gap-2" data-testid={`grupo-cil-cupon-fila-${g.id}-${idx}`}>
                <select value={c.servicio_id} data-testid={`grupo-cil-cupon-servicio-${g.id}-${idx}`}
                  onChange={(e) => cambiarCupon(g.id, idx, "servicio_id", e.target.value)}
                  className="text-sm border border-slate-200 rounded-md px-2 py-1.5 max-w-xs">
                  <option value="">Seleccione un servicio...</option>
                  {servicios.map((s) => (<option key={s.id} value={s.id}>{s.nombre}</option>))}
                </select>
                <Input type="number" min={1} placeholder="km" value={c.km}
                  data-testid={`grupo-cil-cupon-km-${g.id}-${idx}`}
                  onChange={(e) => cambiarCupon(g.id, idx, "km", e.target.value)} className="w-24" />
                <Button size="sm" variant="ghost" data-testid={`grupo-cil-cupon-quitar-${g.id}-${idx}`}
                  className="text-pink-600 hover:bg-pink-50" onClick={() => quitarCupon(g.id, idx)}>Quitar</Button>
              </div>
            ))}
            <Button size="sm" variant="outline" data-testid={`grupo-cil-cupon-agregar-${g.id}`}
              onClick={() => agregarCupon(g.id)}>+ Agregar cupón</Button>
            <div className="flex flex-wrap items-center gap-2 pt-1">
              <label className="text-xs text-slate-600">No consultar cupones después de</label>
              <Input type="number" min={1} value={g.cupones_km_max ?? ""} data-testid={`grupo-cil-km-max-${g.id}`}
                onChange={(e) => setG(g.id, "cupones_km_max", e.target.value)} className="w-24" />
              <span className="text-xs text-slate-600">km</span>
            </div>
          </div>
        </div>
      ))}
      <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-slate-100">
        <Input placeholder="Ej: 150cc" value={nuevo} className="max-w-xs"
          data-testid="grupo-cil-nuevo-nombre" onChange={(e) => setNuevo(e.target.value)} />
        <Button size="sm" data-testid="grupo-cil-crear-btn" onClick={crear} disabled={!nuevo.trim()}
          className="bg-indigo-600 hover:bg-indigo-700 text-white">Agregar grupo</Button>
      </div>
    </div>
  );
}

function GruposTarifa() {
  const [grupos, setGrupos] = useState([]);
  const [nuevo, setNuevo] = useState({ nombre: "", tarifa_hora: "" });

  const cargar = () => api.get("/grupos-tarifa").then((r) => setGrupos(r.data)).catch(() => {});
  useEffect(() => { cargar(); }, []);

  const crear = async () => {
    try {
      await api.post("/grupos-tarifa", { nombre: nuevo.nombre, tarifa_hora: Number(nuevo.tarifa_hora) });
      toast.success("Grupo de tarifa creado");
      setNuevo({ nombre: "", tarifa_hora: "" });
      cargar();
    } catch (e) { toast.error(errorMsg(e)); }
  };

  const actualizar = async (g) => {
    try {
      await api.patch(`/grupos-tarifa/${g.id}`, { nombre: g.nombre, tarifa_hora: Number(g.tarifa_hora) || 0 });
      toast.success("Tarifa actualizada");
      cargar();
    } catch (e) { toast.error(errorMsg(e)); }
  };

  const eliminar = async (g) => {
    if (!window.confirm(`¿Eliminar el grupo "${g.nombre}"? Los modelos asignados quedarán sin grupo.`)) return;
    try { await api.delete(`/grupos-tarifa/${g.id}`); toast.success("Grupo eliminado"); cargar(); }
    catch (e) { toast.error(errorMsg(e)); }
  };

  const setG = (id, campo, v) => setGrupos((prev) => prev.map((g) => (g.id === id ? { ...g, [campo]: v } : g)));

  return (
    <div className="space-y-3" data-testid="grupos-tarifa-lista">
      <p className="text-xs text-slate-500">La mano de obra se calcula como tiempo (F.R.T.) × tarifa del grupo asignado al modelo del vehículo.</p>
      {grupos.map((g) => (
        <div key={g.id} data-testid={`grupo-tarifa-fila-${g.id}`} className="flex flex-wrap items-center gap-3">
          <Input value={g.nombre} onChange={(e) => setG(g.id, "nombre", e.target.value)} className="max-w-xs"
            data-testid={`grupo-tarifa-nombre-${g.id}`} />
          <Input value={g.tarifa_hora} inputMode="decimal" className="w-32"
            data-testid={`grupo-tarifa-hora-${g.id}`}
            onChange={(e) => setG(g.id, "tarifa_hora", e.target.value.replace(/[^\d.]/g, ""))} />
          <span className="text-xs text-slate-400">₡/hora</span>
          <Button size="sm" variant="outline" data-testid={`grupo-tarifa-guardar-${g.id}`}
            className="border-teal-500 text-teal-600 hover:bg-teal-50" onClick={() => actualizar(g)}>Guardar</Button>
          <Button size="sm" variant="outline" data-testid={`grupo-tarifa-eliminar-${g.id}`}
            className="border-pink-300 text-pink-600 hover:bg-pink-50" onClick={() => eliminar(g)}>Eliminar</Button>
        </div>
      ))}
      <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-slate-100">
        <Input placeholder='Ej: Baja cilindrada 110–190cc' value={nuevo.nombre} className="max-w-xs"
          data-testid="grupo-tarifa-nuevo-nombre"
          onChange={(e) => setNuevo({ ...nuevo, nombre: e.target.value })} />
        <Input placeholder="Tarifa ₡/h" value={nuevo.tarifa_hora} inputMode="decimal" className="w-32"
          data-testid="grupo-tarifa-nuevo-tarifa"
          onChange={(e) => setNuevo({ ...nuevo, tarifa_hora: e.target.value.replace(/[^\d.]/g, "") })} />
        <Button size="sm" data-testid="grupo-tarifa-crear-btn" onClick={crear}
          disabled={!nuevo.nombre.trim() || !nuevo.tarifa_hora}
          className="bg-indigo-600 hover:bg-indigo-700 text-white">Agregar grupo</Button>
      </div>
    </div>
  );
}


function PrefijosEquivalentes() {
  const [grupos, setGrupos] = useState([]);
  const [nuevo, setNuevo] = useState({ descripcion: "", prefijos: "" });

  const cargar = () => api.get("/prefijos-equivalentes").then((r) => setGrupos(r.data)).catch(() => {});
  useEffect(() => { cargar(); }, []);

  const parsePrefijos = (s) => (s || "").split(/[^0-9]+/).map((x) => x.trim()).filter(Boolean);

  const crear = async () => {
    try {
      await api.post("/prefijos-equivalentes", { descripcion: nuevo.descripcion, prefijos: parsePrefijos(nuevo.prefijos) });
      toast.success("Grupo de equivalencia creado");
      setNuevo({ descripcion: "", prefijos: "" });
      cargar();
    } catch (e) { toast.error(errorMsg(e)); }
  };

  const actualizar = async (g) => {
    try {
      await api.patch(`/prefijos-equivalentes/${g.id}`, { descripcion: g.descripcion, prefijos: parsePrefijos(g.prefijosTxt) });
      toast.success("Grupo actualizado"); cargar();
    } catch (e) { toast.error(errorMsg(e)); }
  };

  const eliminar = async (g) => {
    if (!window.confirm(`¿Eliminar el grupo "${g.descripcion || g.prefijos.join(", ")}"?`)) return;
    try { await api.delete(`/prefijos-equivalentes/${g.id}`); toast.success("Grupo eliminado"); cargar(); }
    catch (e) { toast.error(errorMsg(e)); }
  };

  const setG = (id, campo, v) => setGrupos((prev) => prev.map((g) => (g.id === id ? { ...g, [campo]: v } : g)));

  return (
    <div className="space-y-3" data-testid="prefijos-equiv-lista">
      <p className="text-xs text-slate-500">
        Cuando el mismo tipo de repuesto cambia de prefijo por un dígito según el modelo (ej. 11393 y 11394),
        agrúpelos aquí. Al generar plantillas con IA se buscará en el catálogo por cualquiera de los prefijos del grupo.
      </p>
      {grupos.map((g) => (
        <div key={g.id} data-testid={`prefijo-equiv-fila-${g.id}`} className="flex flex-wrap items-center gap-3">
          <Input value={g.descripcion || ""} placeholder="Descripción (ej. Empaque tapa de clutch)"
            className="max-w-xs" data-testid={`prefijo-equiv-desc-${g.id}`}
            onChange={(e) => setG(g.id, "descripcion", e.target.value)} />
          <Input value={g.prefijosTxt !== undefined ? g.prefijosTxt : (g.prefijos || []).join(", ")}
            placeholder="11393, 11394" className="w-48 font-mono" data-testid={`prefijo-equiv-prefijos-${g.id}`}
            onChange={(e) => setG(g.id, "prefijosTxt", e.target.value)} />
          <Button size="sm" variant="outline" data-testid={`prefijo-equiv-guardar-${g.id}`}
            className="border-teal-500 text-teal-600 hover:bg-teal-50" onClick={() => actualizar(g)}>Guardar</Button>
          <Button size="sm" variant="outline" data-testid={`prefijo-equiv-eliminar-${g.id}`}
            className="border-pink-300 text-pink-600 hover:bg-pink-50" onClick={() => eliminar(g)}>Eliminar</Button>
        </div>
      ))}
      <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-slate-100">
        <Input placeholder="Descripción (opcional)" value={nuevo.descripcion} className="max-w-xs"
          data-testid="prefijo-equiv-nuevo-desc" onChange={(e) => setNuevo({ ...nuevo, descripcion: e.target.value })} />
        <Input placeholder="Prefijos: 11393, 11394" value={nuevo.prefijos} className="w-48 font-mono"
          data-testid="prefijo-equiv-nuevo-prefijos" onChange={(e) => setNuevo({ ...nuevo, prefijos: e.target.value })} />
        <Button size="sm" data-testid="prefijo-equiv-crear-btn" onClick={crear}
          disabled={parsePrefijos(nuevo.prefijos).length < 2}
          className="bg-indigo-600 hover:bg-indigo-700 text-white">Agregar grupo</Button>
      </div>
    </div>
  );
}

const MOMENTOS_FOTO = [
  { value: "ingreso", label: "Al ingreso" },
  { value: "salida", label: "A la salida" },
  { value: "ambos", label: "Ambos momentos" },
];

function PuntosFoto() {
  const { isAdmin } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [guardandoCfg, setGuardandoCfg] = useState(false);
  const [puntos, setPuntos] = useState([]);
  const [servicios, setServicios] = useState([]);
  const [nuevo, setNuevo] = useState({ nombre: "", momento: "ingreso", obligatorio: false, multiples: false, servicio_ids: [] });

  const cargar = () => {
    api.get("/config-fotos").then((r) => setCfg(r.data)).catch(() => {});
    api.get("/puntos-foto").then((r) => setPuntos(r.data)).catch(() => {});
    api.get("/servicios").then((r) => setServicios(r.data)).catch(() => {});
  };
  useEffect(() => { if (isAdmin) cargar(); }, [isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isAdmin || !cfg) return null;

  const guardarInterruptor = async (campo, v) => {
    setGuardandoCfg(true);
    try {
      const r = await api.put("/config-fotos", {
        fotos_exigir_ingreso_para_cerrar: campo === "fotos_exigir_ingreso_para_cerrar" ? v : !!cfg.fotos_exigir_ingreso_para_cerrar,
        fotos_exigir_salida_para_boleta: campo === "fotos_exigir_salida_para_boleta" ? v : !!cfg.fotos_exigir_salida_para_boleta,
      });
      setCfg(r.data);
      toast.success("Configuración de fotos guardada");
    } catch (e) { toast.error(errorMsg(e)); } finally { setGuardandoCfg(false); }
  };

  const crear = async () => {
    try {
      await api.post("/puntos-foto", { nombre: nuevo.nombre, momento: nuevo.momento, orden: puntos.length + 1,
        obligatorio: nuevo.obligatorio, multiples: nuevo.multiples, servicio_ids: nuevo.servicio_ids });
      toast.success("Punto de foto creado");
      setNuevo({ nombre: "", momento: "ingreso", obligatorio: false, multiples: false, servicio_ids: [] });
      cargar();
    } catch (e) { toast.error(errorMsg(e)); }
  };

  const actualizar = async (p) => {
    try {
      await api.patch(`/puntos-foto/${p.id}`, { nombre: p.nombre, momento: p.momento, orden: Number(p.orden) || 0,
        activo: p.activo, obligatorio: !!p.obligatorio, multiples: !!p.multiples, servicio_ids: p.servicio_ids || [] });
      toast.success("Punto de foto actualizado");
      cargar();
    } catch (e) { toast.error(errorMsg(e)); }
  };

  const eliminar = async (p) => {
    if (!window.confirm(`¿Eliminar el punto "${p.nombre}"?`)) return;
    try { await api.delete(`/puntos-foto/${p.id}`); toast.success("Punto de foto eliminado"); cargar(); }
    catch (e) { toast.error(errorMsg(e)); }
  };

  const setP = (id, campo, v) => setPuntos((prev) => prev.map((p) => (p.id === id ? { ...p, [campo]: v } : p)));
  const servicioNombre = (id) => servicios.find((s) => s.id === id)?.nombre || id;

  return (
    <Panel titulo="Puntos de foto" testId="puntos-foto-panel">
      <p className="text-xs text-slate-500 mb-3">
        Fotos del trabajo: puntos que el asesor debe fotografiar al ingresar o a la salida. "Obligatorio"
        exige la foto; si no elige servicios, el punto aplica a todos los trabajos.
      </p>
      <div className="flex flex-col gap-2 mb-4 pb-4 border-b border-slate-100">
        <div className="flex items-center gap-3">
          <Switch data-testid="config-fotos-exigir-ingreso-switch" checked={!!cfg.fotos_exigir_ingreso_para_cerrar}
            disabled={guardandoCfg} onCheckedChange={(v) => guardarInterruptor("fotos_exigir_ingreso_para_cerrar", v)} />
          <span className="text-sm text-slate-700">Exigir fotos de ingreso obligatorias para cerrar el trabajo</span>
        </div>
        <div className="flex items-center gap-3">
          <Switch data-testid="config-fotos-exigir-salida-switch" checked={!!cfg.fotos_exigir_salida_para_boleta}
            disabled={guardandoCfg} onCheckedChange={(v) => guardarInterruptor("fotos_exigir_salida_para_boleta", v)} />
          <span className="text-sm text-slate-700">Exigir fotos de salida obligatorias para la Boleta de salida / Reporte Final</span>
        </div>
      </div>
      <div className="space-y-3" data-testid="puntos-foto-lista">
        {puntos.map((p) => (
          <div key={p.id} data-testid={`punto-foto-fila-${p.id}`}
            className="flex flex-wrap items-center gap-2.5 rounded-lg border border-slate-100 p-2.5">
            <Switch data-testid={`punto-foto-activo-${p.id}`} checked={p.activo}
              onCheckedChange={(v) => { setP(p.id, "activo", v); actualizar({ ...p, activo: v }); }} />
            <Input value={p.nombre} onChange={(e) => setP(p.id, "nombre", e.target.value)} className="max-w-xs"
              data-testid={`punto-foto-nombre-${p.id}`} />
            <Select value={p.momento} onValueChange={(v) => setP(p.id, "momento", v)}>
              <SelectTrigger data-testid={`punto-foto-momento-${p.id}`} className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MOMENTOS_FOTO.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input value={p.orden} inputMode="numeric" className="w-16" data-testid={`punto-foto-orden-${p.id}`}
              onChange={(e) => setP(p.id, "orden", e.target.value.replace(/\D/g, ""))} />
            <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer select-none">
              <Checkbox data-testid={`punto-foto-obligatorio-${p.id}`} checked={!!p.obligatorio}
                onCheckedChange={(v) => setP(p.id, "obligatorio", !!v)} />
              Obligatorio
            </label>
            <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer select-none">
              <Checkbox data-testid={`punto-foto-multiples-${p.id}`} checked={!!p.multiples}
                onCheckedChange={(v) => setP(p.id, "multiples", !!v)} />
              Permite varias fotos
            </label>
            <div className="w-full sm:w-64">
              <MultiCombobox testId={`punto-foto-servicios-${p.id}`} values={p.servicio_ids || []}
                onChange={(v) => setP(p.id, "servicio_ids", v)} placeholder="Todos los servicios"
                options={servicios.map((s) => ({ value: s.id, label: s.nombre }))} />
            </div>
            {(p.servicio_ids || []).length > 0 && (
              <div className="flex flex-wrap gap-1 basis-full">
                {(p.servicio_ids || []).map((sid) => (
                  <span key={sid} data-testid={`punto-foto-servicio-item-${p.id}-${sid}`}
                    className="inline-flex items-center gap-1 rounded-full bg-slate-100 text-slate-600 text-[11px] px-2 py-0.5">
                    {servicioNombre(sid)}
                    <button type="button" onClick={() => setP(p.id, "servicio_ids", (p.servicio_ids || []).filter((x) => x !== sid))}>
                      <X size={10} />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <Button size="sm" variant="outline" data-testid={`punto-foto-guardar-${p.id}`}
              className="border-teal-500 text-teal-600 hover:bg-teal-50" onClick={() => actualizar(p)}>Guardar</Button>
            <Button size="sm" variant="outline" data-testid={`punto-foto-eliminar-${p.id}`}
              className="border-pink-300 text-pink-600 hover:bg-pink-50" onClick={() => eliminar(p)}>Eliminar</Button>
          </div>
        ))}
        <div className="flex flex-wrap items-center gap-2.5 pt-2 border-t border-slate-100">
          <Input placeholder="Nombre del punto (ej. Caliper trasero)" value={nuevo.nombre} className="max-w-xs"
            data-testid="punto-foto-nuevo-nombre" onChange={(e) => setNuevo({ ...nuevo, nombre: e.target.value })} />
          <Select value={nuevo.momento} onValueChange={(v) => setNuevo({ ...nuevo, momento: v })}>
            <SelectTrigger data-testid="punto-foto-nuevo-momento" className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              {MOMENTOS_FOTO.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer select-none">
            <Checkbox data-testid="punto-foto-nuevo-obligatorio" checked={nuevo.obligatorio}
              onCheckedChange={(v) => setNuevo({ ...nuevo, obligatorio: !!v })} />
            Obligatorio
          </label>
          <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer select-none">
            <Checkbox data-testid="punto-foto-nuevo-multiples" checked={nuevo.multiples}
              onCheckedChange={(v) => setNuevo({ ...nuevo, multiples: !!v })} />
            Permite varias fotos
          </label>
          <div className="w-full sm:w-64">
            <MultiCombobox testId="punto-foto-nuevo-servicios" values={nuevo.servicio_ids}
              onChange={(v) => setNuevo({ ...nuevo, servicio_ids: v })} placeholder="Todos los servicios"
              options={servicios.map((s) => ({ value: s.id, label: s.nombre }))} />
          </div>
          <Button size="sm" data-testid="punto-foto-crear-btn" onClick={crear} disabled={!nuevo.nombre.trim()}
            className="bg-indigo-600 hover:bg-indigo-700 text-white">Agregar punto</Button>
        </div>
      </div>
    </Panel>
  );
}
